import { Fragment, type ReactNode } from "react";

/** The five gauge hues, swept clockwise from 12 o'clock — the Key Dials mark. */
const RING = ["#3eb034", "#f5a51c", "#f97316", "#e7161b", "#94a3b8"];

function polar(cx: number, cy: number, r: number, a: number): [number, number] {
  return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
}

function openArc(cx: number, cy: number, r: number, a0: number, a1: number): string {
  const [x0, y0] = polar(cx, cy, r, a0);
  const [x1, y1] = polar(cx, cy, r, a1);
  const large = a1 - a0 > Math.PI ? 1 : 0;
  return `M ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1}`;
}

/**
 * The KD dial badge as SVG children, scaled to a hub of radius `r` centred at (cx, cy).
 * Shared by the header lockup and the dial's centre hub so the two never drift.
 */
export function kdBadge(cx: number, cy: number, r: number, key?: string): ReactNode {
  const ringR = r * 0.82;
  const stroke = r * 0.16;
  const seg = (Math.PI * 2) / RING.length;
  const gap = seg * 0.16;
  return (
    <Fragment key={key}>
      {RING.map((colour, i) => {
        const a0 = -Math.PI / 2 + i * seg + gap / 2;
        const a1 = -Math.PI / 2 + (i + 1) * seg - gap / 2;
        return (
          <path
            key={i}
            d={openArc(cx, cy, ringR, a0, a1)}
            fill="none"
            stroke={colour}
            strokeWidth={stroke}
            strokeLinecap="round"
          />
        );
      })}
      <text
        x={cx}
        y={cy}
        textAnchor="middle"
        dominantBaseline="central"
        fontFamily="var(--font-display)"
        fontWeight={800}
        fontSize={r * 0.74}
        letterSpacing={-r * 0.02}
        fill="var(--navy)"
      >
        KD
      </text>
    </Fragment>
  );
}

/** Standalone circular KD badge (e.g. small UI affordances). */
export function KdBadge({ size = 40 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" aria-hidden="true">
      {kdBadge(20, 20, 18)}
    </svg>
  );
}

/**
 * The horizontal "Key Dials" lockup for the header. Themes via --navy.
 *
 * `wordmark={false}` drops the words and keeps the badge, in a square box. That exists for the phone-width
 * header, where the lockup is 166px of the ~270px a 320px screen has to spend and the controls beside it
 * need the room — so the brand shrinks to its mark rather than the controls disappearing. It keeps the same
 * `aria-label` either way: the name of the thing is unchanged, only how much of it is drawn.
 */
export function KdLogo({ height = 38, wordmark = true }: { height?: number; wordmark?: boolean }) {
  const width = wordmark ? height * 4.6 : height;
  return (
    <svg
      width={width}
      height={height}
      viewBox={wordmark ? "0 0 184 40" : "0 0 40 40"}
      role="img"
      aria-label="Key Dials"
      style={{ display: "block" }}
    >
      {kdBadge(20, 20, 18)}
      {wordmark && (
        <text
          x={48}
          y={21}
          dominantBaseline="central"
          fontFamily="var(--font-display)"
          fontWeight={800}
          fontSize={21}
          letterSpacing={-0.4}
          fill="var(--navy)"
        >
          Key Dials
        </text>
      )}
    </svg>
  );
}
